"use client"
import type React from "react"
import { useState, useEffect, useCallback } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Spinner,
  Alert,
  FormFeedback,
} from "reactstrap"
import { Btn } from "../../../AbstractElements"
import { MenuService, DropdownService } from "../../../Service/role-management-service"
import type { Menu, CreateMenuRequest, DropdownOption } from "../rolemanagement/types"
import { toast } from "react-toastify"

// Directly define the dropdown menu options for URL paths
const dropdownmenu = [
  { value: "/dashboard", label: "Dashboard" },
  { value: "/users", label: "Users" },
  { value: "/identification", label: "Identification" },
  { value: "/standcategory", label: "Stand Category" },
  { value: "/exhibitiontype", label: "Exhibition Type" },
  { value: "/exhibitor-Type", label: "Exhibitor Type" },
  { value: "/roles", label: "Roles" },
  { value: "/menus", label: "Menus" },
  { value: "/turnstiles", label: "Turnstiles" },
  { value: "/turnstile-assignments", label: "Turnstile Assignment" },
  { value: "/gate-assignments", label: "Gate Assignment" },
  { value: "/exhibition", label: "Exhibition" },
  { value: "/exhibition-statistics", label: "Exhibition Statistics" },
  { value: "/exhibitors", label: "Exhibitors" },
  { value: "/access-management", label: "Accessibility" },
  { value: "/exhibitors-management", label: "Manage Companies" },
  { value: "/badge-requests", label: "Badge Requests" },
  { value: "/printing-badges", label: "Printing Badges" },
  { value: "/orders-verification", label: "Verification" },
  { value: "/badge-entries", label: "Badge Entries" },
  { value: "/ticketing", label: "Ticketing" },
  { value: "/vehicle-visits", label: "Vehicle Entries" },
  { value: "/badge-reporting", label: "Badge Reporting" },
  { value: "/entry-reporting", label: "Entry Reporting" },
  { value: "/my-badge-orders", label: "My Badge Orders" },
  { value: "/staff-cars", label: "Staff Cars" },
  { value: "/ticket-purchase", label: "Purchase Ticket"},
  { value: "/performance", label: "Performance"},
  { value: "/analytics", label: "Analytics" },
]

interface MenuModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialData?: Menu | null
}

interface FormErrors {
  name?: string
  title?: string
  url?: string
  parent?: string
  sort_order?: string
  general?: string
}

export default function MenuModal({ isOpen, onClose, onSuccess, initialData }: MenuModalProps) {
  const [loading, setLoading] = useState(false)
  const [parentMenus, setParentMenus] = useState<DropdownOption[]>([])
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [formData, setFormData] = useState<CreateMenuRequest>({
    name: "",
    title: "",
    url: "",
    parent: undefined,
    icon: "",
    sort_order: 0,
    is_visible: true,
    is_active: true,
    menu_type: "link",
    lan_class: "",
  })
  const isEdit = !!initialData

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchParentMenus()
      if (initialData) {
        setFormData({
          name: initialData.name,
          title: initialData.title,
          url: initialData.url || "", // Use initialData.url directly
          parent: initialData.parent || undefined,
          icon: initialData.icon || "",
          sort_order: initialData.sort_order,
          is_visible: initialData.is_visible,
          is_active: initialData.is_active,
          menu_type: initialData.menu_type,
          lan_class: initialData.lan_class || "",
        })
      } else {
        resetForm()
      }
      setErrors({})
      setTouched({})
    }
  }, [isOpen, initialData])

  const fetchParentMenus = async () => {
    try {
      const menus = await DropdownService.getMenus()
      // Show all menus without filtering by type
      const filteredMenus = initialData ? menus.filter((menu) => menu.id !== initialData.id) : menus
      setParentMenus(filteredMenus)
    } catch (error) {
      console.error("Failed to load parent menus:", error)
      toast.error("Failed to load parent menus")
    }
  }

  const resetForm = () => {
    setFormData({
      name: "",
      title: "",
      url: "",
      parent: undefined,
      icon: "",
      sort_order: 0,
      is_visible: true,
      is_active: true,
      menu_type: "link",
      lan_class: "",
    })
  }

  // Handle input changes with validation
  const handleInputChange = useCallback(
    (field: keyof CreateMenuRequest, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }))
      // Mark field as touched
      setTouched((prev) => ({ ...prev, [field]: true }))
      // Clear error when user starts typing
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }
      // Auto-generate name from title if name is empty
      if (field === "title" && !formData.name) {
        const generatedName = value
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .replace(/\s+/g, "_")
          .trim()
        setFormData((prev) => ({ ...prev, name: generatedName }))
      }
    },
    [formData.name, errors],
  )

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {}
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Menu name is required"
    } else if (!/^[a-z0-9_]+$/.test(formData.name.trim())) {
      newErrors.name = "Name must contain only lowercase letters, numbers, and underscores"
    }
    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = "Menu title is required"
    } else if (formData.title.trim().length < 2) {
      newErrors.title = "Title must be at least 2 characters"
    }
    // URL validation for link type
    if (formData.menu_type === "link" && formData.url) {
      if (!/^\//.test(formData.url)) {
        newErrors.url = "URL must start with /"
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }
    setLoading(true)
    setErrors({})
    try {
      if (isEdit && initialData) {
        await MenuService.update(initialData.id, formData)
        toast.success("Menu updated successfully")
      } else {
        await MenuService.create(formData)
        toast.success("Menu created successfully")
      }
      onSuccess()
    } catch (error: any) {
      console.error("Menu save error:", error)
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors)
      } else {
        setErrors({ general: error.message || "Failed to save menu" })
      }
    } finally {
      setLoading(false)
    }
  }

  const menuTypeOptions = [
    { value: "section", label: "Section", description: "Top-level menu section" },
    { value: "sub", label: "Submenu", description: "Submenu with child items" },
    { value: "link", label: "Link", description: "Direct navigation link" },
  ]

  const commonIcons = [
    // No Icon
    { value: "", label: "No Icon" },
    { value: "widget", label: "Widget" },
    { value: "star", label: "Star" },
    // Navigation & Dashboard
    { value: "home", label: "Home" },
    { value: "dashboard", label: "Dashboard" },
    { value: "menu", label: "Menu" },
    { value: "sidebar", label: "Sidebar" },
    { value: "navigation", label: "Navigation" },
    // User & People
    { value: "user", label: "User" },
    { value: "users", label: "Users" },
    { value: "profile", label: "Profile" },
    { value: "contact", label: "Contact" },
    { value: "team", label: "Team" },
    { value: "group", label: "Group" },
    { value: "person", label: "Person" },
    // Settings & Configuration
    { value: "widget", label: "Widget" },
    { value: "settings", label: "Settings" },
    { value: "cog", label: "Cog" },
    { value: "gear", label: "Gear" },
    { value: "config", label: "Config" },
    { value: "tools", label: "Tools" },
    { value: "wrench", label: "Wrench" },
    // Business & Projects
    { value: "project", label: "Project" },
    { value: "briefcase", label: "Briefcase" },
    { value: "building", label: "Building" },
    { value: "office", label: "Office" },
    { value: "business", label: "Business" },
    // Layout & Design
    { value: "layout", label: "Layout" },
    { value: "grid", label: "Grid" },
    { value: "columns", label: "Columns" },
    { value: "rows", label: "Rows" },
    { value: "template", label: "Template" },
    // E-commerce & Shopping
    { value: "ecommerce", label: "Ecommerce" },
    { value: "shopping-cart", label: "Shopping Cart" },
    { value: "cart", label: "Cart" },
    { value: "product", label: "Product" },
    { value: "shop", label: "Shop" },
    { value: "store", label: "Store" },
    { value: "payment", label: "Payment" },
    { value: "credit-card", label: "Credit Card" },
    { value: "money", label: "Money" },
    { value: "dollar", label: "Dollar" },
    // Communication
    { value: "email", label: "Email" },
    { value: "mail", label: "Mail" },
    { value: "envelope", label: "Envelope" },
    { value: "chat", label: "Chat" },
    { value: "message", label: "Message" },
    { value: "comment", label: "Comment" },
    { value: "phone", label: "Phone" },
    { value: "mobile", label: "Mobile" },
    // Files & Documents
    { value: "file", label: "File" },
    { value: "folder", label: "Folder" },
    { value: "document", label: "Document" },
    { value: "filemanager", label: "File Manager" },
    { value: "archive", label: "Archive" },
    { value: "download", label: "Download" },
    { value: "upload", label: "Upload" },
    // Charts & Analytics
    { value: "charts", label: "Charts" },
    { value: "chart", label: "Chart" },
    { value: "analytics", label: "Analytics" },
    { value: "graph", label: "Graph" },
    { value: "stats", label: "Stats" },
    { value: "report", label: "Report" },
    { value: "pie-chart", label: "Pie Chart" },
    { value: "bar-chart", label: "Bar Chart" },
    // Calendar & Time
    { value: "calendar", label: "Calendar" },
    { value: "clock", label: "Clock" },
    { value: "time", label: "Time" },
    { value: "schedule", label: "Schedule" },
    { value: "date", label: "Date" },
    // Media & Gallery
    { value: "gallery", label: "Gallery" },
    { value: "image", label: "Image" },
    { value: "photo", label: "Photo" },
    { value: "video", label: "Video" },
    { value: "camera", label: "Camera" },
    { value: "media", label: "Media" },
    // Forms & UI
    { value: "form", label: "Form" },
    { value: "input", label: "Input" },
    { value: "button", label: "Button" },
    { value: "checkbox", label: "Checkbox" },
    { value: "radio", label: "Radio" },
    { value: "select", label: "Select" },
    // Tables & Data
    { value: "table", label: "Table" },
    { value: "database", label: "Database" },
    { value: "data", label: "Data" },
    { value: "list", label: "List" },
    { value: "grid-view", label: "Grid View" },
    // UI Components
    { value: "ui-kits", label: "UI Kits" },
    { value: "bonus-kit", label: "Bonus Kit" },
    { value: "components", label: "Components" },
    { value: "elements", label: "Elements" },
    { value: "widgets", label: "Widgets" },
    // Icons & Graphics
    { value: "icons", label: "Icons" },
    { value: "flag", label: "Flag" },
    { value: "star", label: "Star" },
    { value: "heart", label: "Heart" },
    { value: "bookmark", label: "Bookmark" },
    // Social & Apps
    { value: "social", label: "Social" },
    { value: "facebook", label: "Facebook" },
    { value: "twitter", label: "Twitter" },
    { value: "instagram", label: "Instagram" },
    { value: "linkedin", label: "LinkedIn" },
    // Tasks & Productivity
    { value: "task", label: "Task" },
    { value: "to-do", label: "To Do" },
    { value: "todo", label: "Todo" },
    { value: "checklist", label: "Checklist" },
    { value: "note", label: "Note" },
    // Search & Navigation
    { value: "search", label: "Search" },
    { value: "filter", label: "Filter" },
    { value: "sort", label: "Sort" },
    { value: "arrow", label: "Arrow" },
    { value: "navigation-arrow", label: "Navigation Arrow" },
    // Learning & Education
    { value: "learning", label: "Learning" },
    { value: "education", label: "Education" },
    { value: "book", label: "Book" },
    { value: "course", label: "Course" },
    { value: "graduation", label: "Graduation" },
    // Job & Career
    { value: "job-search", label: "Job Search" },
    { value: "career", label: "Career" },
    { value: "resume", label: "Resume" },
    { value: "interview", label: "Interview" },
    // Maps & Location
    { value: "maps", label: "Maps" },
    { value: "map", label: "Map" },
    { value: "location", label: "Location" },
    { value: "pin", label: "Pin" },
    { value: "marker", label: "Marker" },
    // Blog & Content
    { value: "blog", label: "Blog" },
    { value: "post", label: "Post" },
    { value: "article", label: "Article" },
    { value: "content", label: "Content" },
    { value: "editor", label: "Editor" },
    { value: "editors", label: "Editors" },
    // Help & Support
    { value: "faq", label: "FAQ" },
    { value: "help", label: "Help" },
    { value: "support", label: "Support" },
    { value: "support-tickets", label: "Support Tickets" },
    { value: "question", label: "Question" },
    { value: "info", label: "Info" },
    // Security & Privacy
    { value: "security", label: "Security" },
    { value: "shield", label: "Shield" },
    { value: "lock", label: "Lock" },
    { value: "key", label: "Key" },
    { value: "privacy", label: "Privacy" },
    // Knowledge & Documentation
    { value: "knowledgebase", label: "Knowledge Base" },
    { value: "documentation", label: "Documentation" },
    { value: "manual", label: "Manual" },
    { value: "guide", label: "Guide" },
    // Sample & Testing
    { value: "sample-page", label: "Sample Page" },
    { value: "test", label: "Test" },
    { value: "demo", label: "Demo" },
    { value: "example", label: "Example" },
    // Others & Miscellaneous
    { value: "others", label: "Others" },
    { value: "misc", label: "Miscellaneous" },
    { value: "general", label: "General" },
    { value: "default", label: "Default" },
    { value: "custom", label: "Custom" },
  ]

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" backdrop={loading ? "static" : true}>
      <ModalHeader toggle={onClose}>{isEdit ? "Edit Menu" : "Create New Menu"}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {errors.general && (
            <Alert color="danger" className="mb-3">
              {errors.general}
            </Alert>
          )}
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="name">
                  Internal Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  invalid={!!(touched.name && errors.name)}
                  placeholder="menu_name"
                  disabled={loading}
                />
                {touched.name && errors.name && <FormFeedback>{errors.name}</FormFeedback>}
                <small className="text-muted">Lowercase, underscores only</small>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="title">
                  Display Title <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  invalid={!!(touched.title && errors.title)}
                  placeholder="Menu Title"
                  disabled={loading}
                />
                {touched.title && errors.title && <FormFeedback>{errors.title}</FormFeedback>}
                <small className="text-muted">User-friendly display name</small>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="menu_type">
                  Menu Type <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="menu_type"
                  value={formData.menu_type}
                  onChange={(e) => handleInputChange("menu_type", e.target.value as any)}
                  disabled={loading}
                >
                  {menuTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} - {option.description}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="parent">Parent Menu</Label>
                <Input
                  type="select"
                  id="parent"
                  value={formData.parent || ""}
                  onChange={(e) =>
                    handleInputChange("parent", e.target.value ? Number.parseInt(e.target.value) : undefined)
                  }
                  disabled={loading}
                >
                  <option value="">No Parent (Root Level)</option>
                  {parentMenus.map((menu) => (
                    <option key={menu.id} value={menu.id}>
                      {menu.title} ({menu.type})
                    </option>
                  ))}
                </Input>
                <small className="text-muted">Select parent for nested menus</small>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="url">URL Path</Label>
                <Input
                  type="select" // Changed to select
                  id="url"
                  value={formData.url}
                  onChange={(e) => handleInputChange("url", e.target.value)}
                  invalid={!!(touched.url && errors.url)}
                  disabled={loading || formData.menu_type !== "link"}
                >
                  <option value="">Select a URL (optional)</option> {/* Default empty option */}
                  {dropdownmenu.map((urlOption) => (
                    <option key={urlOption.value} value={urlOption.value}>
                      {urlOption.label} ({urlOption.value})
                    </option>
                  ))}
                </Input>
                {touched.url && errors.url && <FormFeedback>{errors.url}</FormFeedback>}
                <small className="text-muted">
                  {formData.menu_type === "link"
                    ? "Select a URL for link type"
                    : "Not applicable for sections/submenus"}
                </small>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="icon">Icon</Label>
                <Input
                  type="select"
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => handleInputChange("icon", e.target.value)}
                  disabled={loading}
                >
                  {commonIcons.map((icon) => (
                    <option key={icon.value} value={icon.value}>
                      {icon.label}
                    </option>
                  ))}
                </Input>
                {formData.icon && (
                  <small className="text-muted">
                    <i className={formData.icon}></i> Preview
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="sort_order">Sort Order</Label>
                <Input
                  type="number"
                  id="sort_order"
                  value={formData.sort_order}
                  onChange={(e) => handleInputChange("sort_order", Number.parseInt(e.target.value) || 0)}
                  invalid={!!(touched.sort_order && errors.sort_order)}
                  min="0"
                  disabled={loading}
                />
                {touched.sort_order && errors.sort_order && <FormFeedback>{errors.sort_order}</FormFeedback>}
                <small className="text-muted">Display order (0 = first)</small>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="lan_class">Language Class</Label>
                <Input
                  type="text"
                  id="lan_class"
                  value={formData.lan_class}
                  onChange={(e) => handleInputChange("lan_class", e.target.value)}
                  placeholder="lan-1"
                  disabled={loading}
                />
                <small className="text-muted">Optional CSS class</small>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup className="d-flex flex-column gap-2 mt-4">
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange("is_active", e.target.checked)}
                    disabled={loading}
                  />
                  <Label check for="is_active">
                    Active
                  </Label>
                </div>
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="is_visible"
                    checked={formData.is_visible}
                    onChange={(e) => handleInputChange("is_visible", e.target.checked)}
                    disabled={loading}
                  />
                  <Label check for="is_visible">
                    Visible in Navigation
                  </Label>
                </div>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading && <Spinner size="sm" className="me-2" />}
            {isEdit ? "Update Menu" : "Create Menu"}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  )
}
